import React, { useState } from 'react';
import { useLayout } from '../../context/LayoutContext';
import { Icon } from '../common/Icon';
import type { MenuPlacement, ModuleOverrideConfig } from '../../types';
import {
  X,
  Search,
  RotateCcw,
  Undo2,
  Check,
  Layout,
  Sliders,
  Sparkles,
  Shield,
  Wrench,
  HelpCircle,
  EyeOff,
  Filter,
  Trash2,
  Lock,
  Edit3,
  FolderKanban,
} from 'lucide-react';

interface MenuConfiguratorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AVAILABLE_ROLES = [
  { id: 'superadmin', label: 'Szuperadmin', color: 'rose' },
  { id: 'org_admin', label: 'Szervezeti Admin', color: 'amber' },
  { id: 'editor', label: 'Szerkesztő', color: 'blue' },
  { id: 'operator', label: 'Ügyintéző', color: 'emerald' },
  { id: 'auditor', label: 'Auditor', color: 'purple' },
  { id: 'user', label: 'Munkatárs / Látogató', color: 'slate' },
];

const PLACEMENTS: { id: MenuPlacement; label: string; icon: any; desc: string; badgeColor: string }[] = [
  {
    id: 'sidebar',
    label: 'Bal oldalsáv menü',
    icon: Layout,
    desc: 'Bal oldali navigáció kategóriákba rendezve',
    badgeColor: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/40 dark:text-cyan-300 dark:border-indigo-800',
  },
  {
    id: 'header_dropdown',
    label: 'Felső kategória lenyíló',
    icon: Sliders,
    desc: 'Felső fejlécben kategória szerint lenyíló menü',
    badgeColor: 'bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-950/40 dark:text-cyan-300 dark:border-cyan-800',
  },
  {
    id: 'navbar_direct',
    label: 'Felső menüsor (Közvetlen)',
    icon: Sparkles,
    desc: 'Felső fejléc jobb oldalán kiemelt közvetlen gomb',
    badgeColor: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800',
  },
  {
    id: 'navbar_tools',
    label: 'Eszközök lenyíló menü',
    icon: Wrench,
    desc: 'Felső fejléc jobb oldalán az Eszközök (Tools) menüben',
    badgeColor: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800',
  },
  {
    id: 'hidden',
    label: 'Rejtett (Inaktív)',
    icon: EyeOff,
    desc: 'Sehol sem jelenik meg a menükben',
    badgeColor: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800',
  },
];

export const MenuConfiguratorModal: React.FC<MenuConfiguratorModalProps> = ({ isOpen, onClose }) => {
  const {
    categories,
    effectiveModules,
    deletedModules,
    saveAllOverrides,
    resetToCentralDefaults,
    resetToFactoryDefaults,
    deleteModule,
    restoreModule,
    restoreAllModules,
    purgeModule,
    purgeAllDeletedModules,
    hasModuleOperation,
    isSuperAdmin,
    isOrgAdmin,
    globalOverrides,
    modules,
  } = useLayout();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedPlacementFilter, setSelectedPlacementFilter] = useState<string>('all');
  const [showDeletedView, setShowDeletedView] = useState(false);
  // Hatásos modulok aktuális beállításainak másolása draft állapotba
  const buildEffectiveDraft = React.useCallback((): Record<string, ModuleOverrideConfig> => {
    const draft: Record<string, ModuleOverrideConfig> = {};
    effectiveModules.forEach((m) => {
      draft[m.id] = {
        placement: m.placement,
        category: m.category,
        roles: m.roles ? [...m.roles] : [],
        mode: m.mode,
        order: m.order,
        global_title: m.global_title || '',
        custom_title: m.custom_title || '',
      };
    });
    return draft;
  }, [effectiveModules]);

  const [draftOverrides, setDraftOverrides] = useState<Record<string, ModuleOverrideConfig>>(() =>
    buildEffectiveDraft()
  );
  const [savedFeedback, setSavedFeedback] = useState(false);

  // Amikor a modális ablak megnyílik, mindig a valós, hatásos (effectiveModules) beállításokat töltjük be
  React.useEffect(() => {
    if (isOpen) {
      setDraftOverrides(buildEffectiveDraft());
      setShowDeletedView(false);
      setSavedFeedback(false);
    }
  }, [isOpen, buildEffectiveDraft]);

  // Amikor egy korábban törölt elemet visszaállítunk a modál nyitva léte alatt, legyen hozzá draft konfiguráció
  React.useEffect(() => {
    if (!isOpen) return;
    setDraftOverrides((prev) => {
      let changed = false;
      const next = { ...prev };
      effectiveModules.forEach((m) => {
        if (!next[m.id]) {
          next[m.id] = {
            placement: m.placement,
            category: m.category,
            roles: m.roles ? [...m.roles] : [],
            mode: m.mode,
            order: m.order,
            global_title: m.global_title || '',
            custom_title: m.custom_title || '',
          };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [effectiveModules, isOpen]);

  if (!isOpen) return null;

  // Hozzáférés ellenőrzése: ki szerkeszthet egy adott modult?
  // A központi (superadmin) beállítás szerinti szerepkörök – a szervezeti admin ezen belül szűkíthet,
  // bővíteni nem tud (a szerver is így menti: worker/core/layout-overrides.js).
  const centralRolesOf = (modId: string): string[] =>
    globalOverrides?.[modId]?.roles ?? modules.find((m) => m.id === modId)?.roles ?? [];

  const canUserEditModule = (mod: { id: string; category: string; roles?: string[] }) => {
    if (isSuperAdmin) return true;
    if (!centralRolesOf(mod.id).includes('org_admin')) return false;
    return isOrgAdmin;
  };

  // A menüponthoz kiosztható szerepkörök: superadmin mindet; szervezeti admin csak a központilag
  // engedélyezetteket, a superadmint soha (a magasabb jogosultság nem látszik és nem állítható).
  const assignableRoles = (modId: string) =>
    isSuperAdmin
      ? AVAILABLE_ROLES
      : AVAILABLE_ROLES.filter((r) => r.id !== 'superadmin' && centralRolesOf(modId).includes(r.id));

  // Megnevezés módosítása draftban
  const handleTitleChange = (modId: string, newTitle: string) => {
    setDraftOverrides((prev) => {
      const existing = prev[modId] || {};
      if (isSuperAdmin) {
        return {
          ...prev,
          [modId]: {
            ...existing,
            global_title: newTitle,
          },
        };
      } else {
        return {
          ...prev,
          [modId]: {
            ...existing,
            custom_title: newTitle,
          },
        };
      }
    });
  };

  // Egyedi modul név visszaállítása gyárira (szuperadmin)
  const handleResetModuleTitleToFactory = (modId: string) => {
    setDraftOverrides((prev) => {
      const existing = { ...(prev[modId] || {}) };
      delete existing.global_title;
      delete existing.custom_title;
      return { ...prev, [modId]: existing };
    });
  };

  // Egyedi modul név visszaállítása központira (szervezeti admin)
  const handleResetModuleTitleToCentral = (modId: string) => {
    setDraftOverrides((prev) => {
      const existing = { ...(prev[modId] || {}) };
      delete existing.custom_title;
      return { ...prev, [modId]: existing };
    });
  };

  // Helyi draft módosítása egy modulhoz
  const handlePlacementChange = (modId: string, placement: MenuPlacement) => {
    setDraftOverrides((prev) => ({
      ...prev,
      [modId]: {
        ...prev[modId],
        placement,
      },
    }));
  };

  const handleCategoryChange = (modId: string, category: string) => {
    setDraftOverrides((prev) => ({
      ...prev,
      [modId]: {
        ...prev[modId],
        category,
      },
    }));
  };

  const handleCategoryReset = (modId: string) => {
    setDraftOverrides((prev) => {
      const existing = { ...(prev[modId] || {}) };
      delete existing.category;
      return { ...prev, [modId]: existing };
    });
  };

  const handleOrderChange = (modId: string, orderVal: number) => {
    setDraftOverrides((prev) => ({
      ...prev,
      [modId]: {
        ...prev[modId],
        order: orderVal,
      },
    }));
  };

  const handleRoleToggle = (modId: string, roleId: string, currentRoles: string[]) => {
    if (!isSuperAdmin && (roleId === 'superadmin' || roleId === 'org_admin' || !centralRolesOf(modId).includes(roleId))) return;
    const roles = currentRoles.includes(roleId)
      ? currentRoles.filter((r) => r !== roleId)
      : [...currentRoles, roleId];

    setDraftOverrides((prev) => ({
      ...prev,
      [modId]: {
        ...prev[modId],
        roles,
      },
    }));
  };

  const handleModeChange = (modId: string, mode: 'simplified' | 'extended' | 'both') => {
    setDraftOverrides((prev) => ({
      ...prev,
      [modId]: {
        ...prev[modId],
        mode,
      },
    }));
  };

  // Mentés alkalmazása
  const handleSave = () => {
    saveAllOverrides(draftOverrides);
    setSavedFeedback(true);
    setTimeout(() => {
      setSavedFeedback(false);
      onClose();
    }, 1200);
  };

  // Visszaállítás GYÁRI állapotra
  const handleResetToFactory = async () => {
    if (
      confirm(
        'Biztosan visszaállítod az összes menüpontot, megnevezést és elhelyezést a GYÁRI alapértékekre?\n\nEz a forráskód szerinti eredeti alapértékeket állítja vissza minden bérlőnél.'
      )
    ) {
      await resetToFactoryDefaults();
      setDraftOverrides({});
      setSavedFeedback(true);
      setTimeout(() => {
        setSavedFeedback(false);
        onClose();
      }, 1000);
    }
  };

  // Visszaállítás KÖZPONTI beállításra
  const handleResetToCentral = async () => {
    if (
      confirm(
        'Biztosan visszaállítod a szervezeti megnevezéseket és beállításokat a KÖZPONTI beállításra?\n\nA szervezeti egyedi elnevezések törlődnek, és a Szuperadmin által központilag meghatározott beállítások lépnek érvénybe.'
      )
    ) {
      await resetToCentralDefaults();
      setDraftOverrides({});
      setSavedFeedback(true);
      setTimeout(() => {
        setSavedFeedback(false);
        onClose();
      }, 1000);
    }
  };

  // Modul törlése védelmi lánccal: csak ha inaktív ÉS nincs hozzárendelve művelet!
  const handleDeleteModule = (modId: string, modTitle: string) => {
    const op = hasModuleOperation(modId);
    if (op.hasOperation) {
      alert(`A(z) "${modTitle}" menüpont nem törölhető, mert aktív művelet van hozzárendelve: ${op.reason}`);
      return;
    }

    if (
      confirm(
        `Biztosan törölni szeretné a(z) "${modTitle}" menüpontot a rendszerből?\n\nMivel az elem Rejtett (Inaktív) és nem tartozik hozzá semmilyen futtatható művelet, a törlés biztonságosan elvégezhető.\n(A törölt elemek közül bármikor visszaállítható)`
      )
    ) {
      deleteModule(modId);
    }
  };

  const handleRestoreModule = (modId: string) => {
    restoreModule(modId);
  };

  // Szűrt modulok
  const filteredModules = effectiveModules.filter((mod) => {
    // Szervezeti admin csak azokat a menüpontokat látja, amelyeket kezelhet
    if (!isSuperAdmin && !canUserEditModule(mod)) return false;
    const draft = draftOverrides[mod.id];
    const currentPlacement = draft?.placement ?? mod.placement ?? 'sidebar';
    const currentCategory = draft?.category ?? mod.category;

    if (selectedCategory !== 'all' && currentCategory !== selectedCategory) {
      return false;
    }

    if (selectedPlacementFilter !== 'all' && currentPlacement !== selectedPlacementFilter) {
      return false;
    }

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      const matchTitle = mod.title.toLowerCase().includes(q);
      const matchDefTitle = (mod.default_title || '').toLowerCase().includes(q);
      const matchGlobTitle = (mod.global_title || '').toLowerCase().includes(q);
      const matchCustTitle = (mod.custom_title || '').toLowerCase().includes(q);
      const matchDraftCust = (draftOverrides[mod.id]?.custom_title || '').toLowerCase().includes(q);
      const matchDraftGlob = (draftOverrides[mod.id]?.global_title || '').toLowerCase().includes(q);
      const matchDesc = mod.description.toLowerCase().includes(q);
      const matchId = mod.id.toLowerCase().includes(q);
      return matchTitle || matchDefTitle || matchGlobTitle || matchCustTitle || matchDraftCust || matchDraftGlob || matchDesc || matchId;
    }

    return true;
  });

  // Statisztikák számítása a draft szerint
  const stats = {
    sidebar: 0,
    header_dropdown: 0,
    navbar_direct: 0,
    navbar_tools: 0,
    hidden: 0,
  };

  effectiveModules.forEach((m) => {
    const p = draftOverrides[m.id]?.placement ?? m.placement ?? 'sidebar';
    if (p in stats) {
      stats[p as keyof typeof stats]++;
    }
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200 overflow-y-auto">
      <div className="relative w-full max-w-6xl my-auto max-h-[92vh] bg-white dark:bg-[#0d121f] rounded-3xl border border-slate-200 dark:border-slate-800 shadow-2xl flex flex-col overflow-hidden text-slate-800 dark:text-slate-100">
        
        {/* FEJLÉC */}
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-900/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-indigo-500/10 text-indigo-600 dark:text-cyan-400 flex items-center justify-center border border-indigo-500/20 shadow-xs">
              <Sliders className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">
                  Menüpontok, Elhelyezés & Jogosultságok Szabályozása
                </h2>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-950/60 text-indigo-700 dark:text-cyan-300 font-semibold border border-indigo-200 dark:border-indigo-800">
                  Valós idejű vezérlő
                </span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Szabályozd, hogy az egyes funkciók melyik dedikált menüben, milyen szerepköröknek és melyik nézetmódban jelenjenek meg.
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* GYORS STATISZTIKA & SZŰRŐSÁV */}
        <div className="px-6 py-3 bg-slate-100/70 dark:bg-[#0a0e17] border-b border-slate-200 dark:border-slate-800 flex flex-wrap items-center justify-between gap-3">
          {/* Helyszín szerinti gyors szűrők */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs text-slate-400 font-medium mr-1 flex items-center gap-1">
              <Filter className="w-3.5 h-3.5" /> Szűrés:
            </span>
            <button
              onClick={() => setSelectedPlacementFilter('all')}
              className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition cursor-pointer border ${
                selectedPlacementFilter === 'all'
                  ? 'bg-slate-800 text-white border-slate-800 dark:bg-slate-200 dark:text-slate-900'
                  : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-800'
              }`}
            >
              Összes ({effectiveModules.length})
            </button>
            {PLACEMENTS.map((p) => {
              const count = stats[p.id as keyof typeof stats] || 0;
              const isSelected = selectedPlacementFilter === p.id;
              const IconComp = p.icon;

              return (
                <button
                  key={p.id}
                  onClick={() => setSelectedPlacementFilter(isSelected ? 'all' : p.id)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium transition cursor-pointer flex items-center gap-1.5 border ${
                    isSelected
                      ? 'bg-indigo-600 text-white border-indigo-600 dark:bg-cyan-500 dark:text-black font-semibold'
                      : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-800 hover:border-slate-300'
                  }`}
                >
                  <IconComp className="w-3.5 h-3.5" />
                  <span>{p.label}</span>
                  <span className="text-[10px] px-1 rounded-full bg-black/10 dark:bg-white/10 font-mono">
                    {count}
                  </span>
                </button>
              );
            })}

            {deletedModules.length > 0 && (
              <button
                onClick={() => setShowDeletedView(!showDeletedView)}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition cursor-pointer flex items-center gap-1.5 border ${
                  showDeletedView
                    ? 'bg-rose-600 text-white border-rose-600 dark:bg-rose-500 dark:text-black font-semibold shadow-xs'
                    : 'bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800 hover:bg-rose-100'
                }`}
                title="Törölt menüpontok megtekintése és visszaállítása"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Törölt elemek</span>
                <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-rose-200 dark:bg-rose-900 text-rose-800 dark:text-rose-200 font-mono font-bold">
                  {deletedModules.length}
                </span>
              </button>
            )}
          </div>

          {/* Szűrők jobb oldalon: Kategória + Keresés */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              aria-label="Kategória szűrő"
              className="px-2.5 py-1.5 rounded-xl text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 focus:outline-hidden focus:border-indigo-500 cursor-pointer"
            >
              <option value="all">Összes kategória</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>

            <div className="relative w-full sm:w-56">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Funkció keresése..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 rounded-xl text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 focus:outline-hidden focus:border-indigo-500 text-slate-800 dark:text-slate-200 placeholder-slate-400"
              />
            </div>
          </div>
        </div>

        {/* FŐ MODULLISTA GÖRGŐS TARTALOM */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {showDeletedView ? (
            <div className="space-y-3">
              <div className="p-4 rounded-2xl bg-rose-50/60 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/60 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h3 className="font-bold text-sm text-rose-900 dark:text-rose-200 flex items-center gap-2">
                    <Trash2 className="w-4 h-4 text-rose-600" />
                    <span>Törölt / Eltávolított Menüpontok ({deletedModules.length} db)</span>
                  </h3>
                  <p className="text-xs text-rose-700/80 dark:text-rose-300/80 mt-0.5">
                    Ezek a menüpontok jelenleg nem jelennek meg a menükben. A visszaállítás után újra szabadon szabályozható az elhelyezésük és hozzáférési szerepkörük.
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap sm:flex-nowrap">
                  {deletedModules.length > 0 && (
                    <>
                      <button
                        onClick={() => {
                          restoreAllModules();
                          setShowDeletedView(false);
                        }}
                        className="px-3 py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold shadow-xs transition cursor-pointer flex items-center gap-1.5"
                        title="Minden törölt elem visszahozatala az aktív menübe"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        <span>Összes visszaállítása</span>
                      </button>
                      <button
                        onClick={() => {
                          if (
                            confirm(
                              'Biztosan véglegesen törölni szeretnéd az összes elemet a törölt listából?\n\nEzek a funkciók teljesen kikerülnek a rendszerből, így nem fognak többé helyet foglalni a listában.'
                            )
                          ) {
                            purgeAllDeletedModules();
                            setShowDeletedView(false);
                          }
                        }}
                        className="px-3 py-1.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold shadow-xs transition cursor-pointer flex items-center gap-1.5"
                        title="Törölt elemek teljes kiürítése (Végleges törlés)"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        <span>Összes végleges törlése</span>
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => setShowDeletedView(false)}
                    className="px-3 py-1.5 rounded-xl bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-800 text-xs font-medium hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                  >
                    Vissza az aktív menüpontokhoz
                  </button>
                </div>
              </div>

              {deletedModules.length === 0 ? (
                <div className="py-12 text-center text-slate-400 text-xs">Nincsenek törölt menüpontok. Minden modul aktív.</div>
              ) : (
                deletedModules.map((mod) => (
                  <div
                    key={mod.id}
                    className="p-4 rounded-2xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-2xs"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 shrink-0">
                        <Icon name={mod.icon} className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-sm text-slate-900 dark:text-white">{mod.title}</span>
                          <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500">
                            {mod.id}
                          </span>
                          <span className="text-[10px] px-2 py-0.5 rounded bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 font-semibold border border-rose-200 dark:border-rose-900">
                            Törölt / Inaktív
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">{mod.description}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0 flex-wrap sm:flex-nowrap">
                      <button
                        onClick={() => {
                          handleRestoreModule(mod.id);
                          setShowDeletedView(false);
                        }}
                        className="px-3 py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer shadow-xs"
                        title="Menüpont visszaállítása az aktív menübe & szabályozás"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        <span>Visszaállítás & Szabályozás</span>
                      </button>
                      <button
                        onClick={() => {
                          if (
                            confirm(
                              `Biztosan véglegesen törölni szeretnéd a(z) "${mod.title}" menüpontot?\n\nEz a menüpont teljesen kikerül a rendszerből és nem fog többé helyet foglalni a törölt elemek között sem.`
                            )
                          ) {
                            purgeModule(mod.id);
                          }
                        }}
                        className="px-3 py-1.5 rounded-xl bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/40 dark:hover:bg-rose-900/60 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-900 text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer shadow-xs"
                        title="Végleges törlés a törölt elemek közül is"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400" />
                        <span>Végleges törlés</span>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <>
              {deletedModules.length > 0 && (
                <div className="p-3.5 rounded-2xl bg-amber-50/80 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/60 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-amber-900 dark:text-amber-200 text-xs shadow-2xs">
                  <div className="flex items-start sm:items-center gap-2.5">
                    <span className="p-1 rounded-lg bg-amber-200/60 dark:bg-amber-900 text-amber-800 dark:text-amber-300 font-bold shrink-0">
                      ⚠️
                    </span>
                    <div>
                      <span className="font-semibold">Figyelem:</span> {deletedModules.length} db menüpont jelenleg a{' '}
                      <button
                        onClick={() => setShowDeletedView(true)}
                        className="underline font-bold hover:text-amber-700 dark:hover:text-amber-300 cursor-pointer"
                      >
                        Törölt elemek
                      </button>{' '}
                      között található ({deletedModules.map((m) => m.title).join(', ')}).
                      <div className="text-[11px] text-amber-700/90 dark:text-amber-400 mt-0.5">
                        Ha szeretnéd szabályozni (pl. hogy melyik szerepkör vagy bérlői admin láthatja), állítsd vissza őket az aktív listába.
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => restoreAllModules()}
                    className="px-3 py-1.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold text-xs transition cursor-pointer shadow-2xs shrink-0 flex items-center gap-1.5 self-start sm:self-auto"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    <span>Összes visszaállítása</span>
                  </button>
                </div>
              )}

              {filteredModules.length === 0 ? (
                <div className="py-16 text-center text-slate-400 space-y-2">
                  <HelpCircle className="w-10 h-10 mx-auto text-slate-300 dark:text-slate-600" />
                  <p className="text-sm font-medium">Nincs a szűrésnek megfelelő menüpont vagy funkció.</p>
                  <p className="text-xs text-slate-500">Próbálj meg más keresőszót megadni vagy törölni a szűrőket.</p>
                </div>
              ) : (
            filteredModules.map((mod) => {
              const draft = draftOverrides[mod.id];
              const currentPlacement = draft?.placement ?? mod.placement ?? 'sidebar';
              const currentCategory = draft?.category ?? mod.category;
              const currentRoles = draft?.roles ?? mod.roles ?? [];
              const currentMode = draft?.mode ?? mod.mode ?? 'both';
              const currentOrder = draft?.order ?? mod.order ?? 1;
              const defaultCategory = mod.default_category || mod.category;
              const catObj = categories.find((c) => c.id === currentCategory);
              const isCategoryChanged = currentCategory !== defaultCategory;

              const canEdit = canUserEditModule(mod);
              const defaultTitle = mod.default_title || mod.title;
              const centralGlobalTitle = (draft?.global_title !== undefined ? draft.global_title : (mod.global_title || '')).trim();
              const tenantCustomTitle = (draft?.custom_title !== undefined ? draft.custom_title : (mod.custom_title || '')).trim();

              const currentTitleValue = isSuperAdmin
                ? (draft?.global_title !== undefined ? draft.global_title : (mod.global_title || ''))
                : (draft?.custom_title !== undefined ? draft.custom_title : (mod.custom_title || ''));

              const hasCustomGlobal = Boolean(centralGlobalTitle && centralGlobalTitle !== defaultTitle);
              const hasCustomTenant = Boolean(tenantCustomTitle);

              return (
                <div
                  key={mod.id}
                  className="p-4 rounded-2xl bg-slate-50/70 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800/80 hover:border-slate-300 dark:hover:border-slate-700 transition space-y-3.5"
                >
                  {/* Felső sor: Modul azonosító és leírás */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center text-indigo-600 dark:text-cyan-400 shrink-0 mt-0.5">
                        <Icon name={mod.icon} className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-sm text-slate-900 dark:text-white">
                            {mod.title}
                          </span>
                          <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-200/80 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                            {mod.id}
                          </span>
                          {catObj && (
                            <span className={`text-[10px] px-2 py-0.5 rounded flex items-center gap-1 border ${
                              isCategoryChanged
                                ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800 font-semibold'
                                : 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-cyan-300 border border-indigo-100 dark:border-indigo-900'
                            }`}>
                              <Icon name={catObj.icon} className="w-3 h-3" />
                              <span>{catObj.title}</span>
                              {isCategoryChanged && (
                                <span className="text-[9px] font-bold text-amber-600 dark:text-amber-400">(áthelyezve)</span>
                              )}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          {mod.description}
                        </p>
                      </div>
                    </div>

                    {/* Működési mód választó & Sorszám */}
                    <div className="flex items-center gap-2 shrink-0 self-start sm:self-auto flex-wrap sm:flex-nowrap">
                      <div className="flex items-center gap-1 bg-white dark:bg-slate-950 p-1 rounded-xl border border-slate-200 dark:border-slate-800">
                        <button
                          onClick={() => handleModeChange(mod.id, 'simplified')}
                          className={`px-2 py-1 rounded-lg text-[11px] font-medium transition cursor-pointer ${
                            currentMode === 'simplified'
                              ? 'bg-emerald-600 text-white font-bold shadow-xs'
                              : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                          }`}
                          title="Csak az Egyszerűsített módban jelenik meg"
                        >
                          ⚡ Egyszerűsített
                        </button>
                        <button
                          onClick={() => handleModeChange(mod.id, 'extended')}
                          className={`px-2 py-1 rounded-lg text-[11px] font-medium transition cursor-pointer ${
                            currentMode === 'extended'
                              ? 'bg-blue-600 text-white font-bold shadow-xs'
                              : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                          }`}
                          title="Csak az Összetett módban jelenik meg"
                        >
                          🛠️ Összetett
                        </button>
                        <button
                          onClick={() => handleModeChange(mod.id, 'both')}
                          className={`px-2 py-1 rounded-lg text-[11px] font-medium transition cursor-pointer ${
                            currentMode === 'both'
                              ? 'bg-indigo-600 text-white font-bold shadow-xs'
                              : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                          }`}
                          title="Mindkét működési módban látható"
                        >
                          🔄 Mindkettő
                        </button>
                      </div>

                      <div className="flex items-center gap-1.5 bg-white dark:bg-slate-950 px-2.5 py-1.5 rounded-xl border border-slate-200 dark:border-slate-800 text-[11px] text-slate-500 dark:text-slate-400">
                        <span className="font-semibold text-[10px] uppercase">Sorszám:</span>
                        <input
                          type="number"
                          min="1"
                          max="99"
                          value={currentOrder}
                          onChange={(e) => handleOrderChange(mod.id, parseInt(e.target.value) || 1)}
                          className="w-10 px-1 py-0.5 rounded text-center border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 font-mono text-xs focus:outline-hidden focus:border-indigo-500"
                          title="Megjelenési sorszám a kategórián belül"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Középső sor: Megnevezés & Egyedi átnevezés sáv */}
                  <div className="p-2.5 rounded-xl bg-white dark:bg-slate-950/70 border border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-start sm:items-center gap-2.5 min-w-0 flex-1">
                      <div className="p-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-500 shrink-0 mt-0.5 sm:mt-0">
                        <Edit3 className="w-3.5 h-3.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap mb-1">
                          <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                            {isSuperAdmin ? 'Központi megnevezés (Globális):' : 'Szervezeti egyedi megnevezés:'}
                          </span>
                          {isSuperAdmin && hasCustomGlobal && (
                            <span className="text-[9px] px-1.5 py-0.2 rounded bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 font-semibold border border-amber-200 dark:border-amber-800">
                              Központilag módosítva
                            </span>
                          )}
                          {!isSuperAdmin && hasCustomTenant && (
                            <span className="text-[9px] px-1.5 py-0.2 rounded bg-indigo-100 dark:bg-indigo-950/60 text-indigo-700 dark:text-cyan-300 font-semibold border border-indigo-200 dark:border-indigo-800">
                              Szervezeti egyedi név
                            </span>
                          )}
                          {!canEdit && (
                            <span className="text-[9px] px-1.5 py-0.2 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 font-semibold flex items-center gap-1 border border-slate-200 dark:border-slate-700">
                              <Lock className="w-2.5 h-2.5" /> Csak Szuperadmin
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={currentTitleValue}
                            onChange={(e) => handleTitleChange(mod.id, e.target.value)}
                            disabled={!canEdit}
                            placeholder={
                              isSuperAdmin
                                ? `Gyári alapérték: ${defaultTitle}`
                                : `Központi név: ${centralGlobalTitle || defaultTitle}`
                            }
                            className={`w-full max-w-sm sm:max-w-md px-3 py-1.5 rounded-lg text-xs border transition ${
                              !canEdit
                                ? 'bg-slate-100 dark:bg-slate-900 text-slate-400 border-slate-200 dark:border-slate-800 cursor-not-allowed'
                                : 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white border-slate-200 dark:border-slate-700 focus:border-indigo-500 focus:outline-hidden'
                            }`}
                          />

                          {canEdit && (
                            isSuperAdmin ? (
                              hasCustomGlobal && (
                                <button
                                  type="button"
                                  onClick={() => handleResetModuleTitleToFactory(mod.id)}
                                  className="px-2.5 py-1.5 rounded-lg text-xs font-medium text-slate-600 dark:text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 border border-slate-200 dark:border-slate-800 transition flex items-center gap-1 cursor-pointer shrink-0"
                                  title={`Visszaállítás gyári névre: "${defaultTitle}"`}
                                >
                                  <RotateCcw className="w-3 h-3" />
                                  <span>Gyári névre</span>
                                </button>
                              )
                            ) : (
                              hasCustomTenant && (
                                <button
                                  type="button"
                                  onClick={() => handleResetModuleTitleToCentral(mod.id)}
                                  className="px-2.5 py-1.5 rounded-lg text-xs font-medium text-indigo-600 dark:text-cyan-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 transition flex items-center gap-1 cursor-pointer shrink-0"
                                  title="Visszaállítás a Szuperadmin által beállított központi névre"
                                >
                                  <Undo2 className="w-3 h-3" />
                                  <span>Vissza központi névre</span>
                                </button>
                              )
                            )
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="text-[10px] text-slate-400 shrink-0 sm:text-right flex sm:flex-col gap-2 sm:gap-0.5 justify-between">
                      <div>Gyári: <span className="font-mono text-slate-600 dark:text-slate-300 font-medium">{defaultTitle}</span></div>
                      {centralGlobalTitle && centralGlobalTitle !== defaultTitle && (
                        <div>Központi: <span className="font-mono text-indigo-600 dark:text-cyan-400 font-medium">{centralGlobalTitle}</span></div>
                      )}
                    </div>
                  </div>

                  {/* Cél menüblokk / Szekció (Kategória) kiválasztása */}
                  <div className="space-y-1.5 pt-2 border-t border-slate-200/80 dark:border-slate-800/60">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 flex items-center justify-between flex-wrap gap-1">
                      <span className="flex items-center gap-1.5">
                        <FolderKanban className="w-3.5 h-3.5 text-indigo-600 dark:text-cyan-400" />
                        <span>Cél menüblokk / Szekció (Melyik kategória alá tartozzon a menüben?):</span>
                      </span>
                      {isCategoryChanged && (
                        <button
                          type="button"
                          onClick={() => handleCategoryReset(mod.id)}
                          className="text-[10px] text-indigo-600 dark:text-cyan-400 hover:underline cursor-pointer flex items-center gap-1 font-semibold"
                          title="Visszaállítás az eredeti gyári kategóriába"
                        >
                          <RotateCcw className="w-2.5 h-2.5" />
                          <span>Vissza az eredeti blokkba ({categories.find((c) => c.id === defaultCategory)?.title || defaultCategory})</span>
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-1.5">
                      {categories.map((c) => {
                        const isCurrentCat = currentCategory === c.id;
                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => handleCategoryChange(mod.id, c.id)}
                            className={`px-2.5 py-1.5 rounded-xl text-xs font-medium flex items-center gap-2 transition cursor-pointer border text-left ${
                              isCurrentCat
                                ? 'bg-indigo-50 border-indigo-300 text-indigo-700 dark:bg-indigo-950/60 dark:text-cyan-300 dark:border-indigo-700 font-bold shadow-xs ring-1 ring-indigo-500/30'
                                : 'bg-white dark:bg-slate-950 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-800/80 hover:bg-slate-100 dark:hover:bg-slate-800'
                            }`}
                            title={`${c.title} kategória blokkba helyezés`}
                          >
                            <Icon name={c.icon} className={`w-3.5 h-3.5 shrink-0 ${isCurrentCat ? 'text-indigo-600 dark:text-cyan-400' : 'text-slate-400'}`} />
                            <span className="truncate">{c.title}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Alsó sor: 1. Megjelenési hely kiválasztása, 2. Szerepkörök */}
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 pt-2 border-t border-slate-200/80 dark:border-slate-800/60">
                    {/* Helyszín választó (5 opció) */}
                    <div className="lg:col-span-6 space-y-1">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 flex items-center gap-1">
                        <Layout className="w-3 h-3" /> Megjelenési helyszín (4 dedikált hely):
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                        {PLACEMENTS.map((p) => {
                          const isCurrent = currentPlacement === p.id;
                          const IconComp = p.icon;

                          return (
                            <button
                              key={p.id}
                              onClick={() => handlePlacementChange(mod.id, p.id)}
                              className={`px-2.5 py-1.5 rounded-xl text-xs font-medium flex items-center gap-1.5 transition cursor-pointer border text-left ${
                                isCurrent
                                  ? `${p.badgeColor} font-bold shadow-xs ring-1 ring-indigo-500/20`
                                  : 'bg-white dark:bg-slate-950 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-800/80 hover:bg-slate-100 dark:hover:bg-slate-800'
                              }`}
                              title={p.desc}
                            >
                              <IconComp className="w-3.5 h-3.5 shrink-0" />
                              <span className="truncate">{p.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Szerepkör választó (RBAC) */}
                    <div className="lg:col-span-6 space-y-1">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 flex items-center justify-between">
                        <span className="flex items-center gap-1">
                          <Shield className="w-3 h-3" /> Láthatja & használhatja (Szerepkörök):
                        </span>
                        <span className="text-[9px] text-slate-400">
                          {assignableRoles(mod.id).filter((r) => currentRoles.includes(r.id) || (!isSuperAdmin && r.id === 'org_admin')).length} szerepkör kijelölve
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {assignableRoles(mod.id).map((r) => {
                          // A szervezeti admin a saját hozzáférését nem veheti el (ne zárja ki magát)
                          const isLocked = !isSuperAdmin && r.id === 'org_admin';
                          const isEnabled = isLocked || currentRoles.includes(r.id);

                          return (
                            <button
                              key={r.id}
                              type="button"
                              disabled={isLocked}
                              onClick={() => handleRoleToggle(mod.id, r.id, currentRoles)}
                              className={`px-2 py-1 rounded-lg text-xs font-medium transition cursor-pointer border flex items-center gap-1 ${
                                isEnabled
                                  ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 border-slate-900 dark:border-white font-semibold shadow-2xs'
                                  : 'bg-white dark:bg-slate-950 text-slate-400 dark:text-slate-500 border-slate-200 dark:border-slate-800 hover:border-slate-300'
                              }`}
                              title={isLocked ? 'A szervezeti admin hozzáférése nem vehető el' : `${r.label} szerepkör hozzáférése`}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full ${isEnabled ? 'bg-emerald-400' : 'bg-slate-400'}`} />
                              <span>{r.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Védelmi lánc: Inaktív modul törlése, ha nincs hozzárendelve művelet */}
                  {currentPlacement === 'hidden' && (() => {
                    const opInfo = hasModuleOperation(mod.id);
                    if (!opInfo.hasOperation) {
                      return (
                        <div className="pt-2.5 mt-1 border-t border-rose-200/80 dark:border-rose-900/60 flex items-center justify-between gap-3 flex-wrap bg-rose-50/60 dark:bg-rose-950/20 p-2.5 rounded-xl">
                          <div className="flex items-center gap-2 text-xs text-rose-800 dark:text-rose-300">
                            <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                            <span>
                              Ez a menüpont <strong>Inaktív (Rejtett)</strong> és <strong>nincs hozzárendelve művelet</strong>, ezért biztonságosan törölhető a rendszerből.
                            </span>
                          </div>
                          <button
                            onClick={() => handleDeleteModule(mod.id, mod.title)}
                            className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-500 active:bg-rose-700 text-white text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer shadow-xs shrink-0"
                            title="Menüpont végleges törlése"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            <span>Menüpont törlése</span>
                          </button>
                        </div>
                      );
                    } else {
                      return (
                        <div className="pt-2.5 mt-1 border-t border-amber-200/80 dark:border-amber-900/60 flex items-center justify-between gap-3 flex-wrap bg-amber-50/60 dark:bg-amber-950/20 p-2.5 rounded-xl">
                          <div className="flex items-center gap-2 text-xs text-amber-800 dark:text-amber-300">
                            <Lock className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
                            <span>
                              <strong>Törlés védett:</strong> Ehhez a funkcióhoz aktív rendszerfolyamat kapcsolódik (<em>{opInfo.reason}</em>). Csak elrejteni lehet, törölni nem.
                            </span>
                          </div>
                          <button
                            disabled
                            className="px-2.5 py-1.5 rounded-lg bg-slate-200 dark:bg-slate-800 text-slate-400 dark:text-slate-500 text-xs font-medium flex items-center gap-1.5 cursor-not-allowed shrink-0 opacity-75"
                            title={`Nem törölhető: ${opInfo.reason}`}
                          >
                            <Lock className="w-3.5 h-3.5" />
                            <span>Törlés tiltva</span>
                          </button>
                        </div>
                      );
                    }
                  })()}
                </div>
              );
            })
          )}
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-900/80 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Visszaállítás a központi beállításra (csak nem-szuperadmin szerepköröknek, akik a központi szuperadmin konfigurációra akarnak visszaállni) */}
            {!isSuperAdmin && (
              <button
                type="button"
                onClick={handleResetToCentral}
                className="px-3 py-2 rounded-xl text-xs text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800/80 transition flex items-center gap-1.5 cursor-pointer font-medium"
                title="Szervezeti egyedi nevek törlése és visszaállása a Szuperadmin központi beállításaira"
              >
                <Undo2 className="w-3.5 h-3.5" />
                <span>Vissza a központi beállításra</span>
              </button>
            )}

            {/* Visszaállítás a gyári alapértékekre – csak superadmin (a szervezeti adminnak a
                „Vissza a központi beállításra” ugyanezt teszi a saját szervezetére) */}
            {isSuperAdmin && (
            <button
              type="button"
              onClick={handleResetToFactory}
              className="px-3 py-2 rounded-xl text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/30 border border-transparent hover:border-rose-200 dark:hover:border-rose-900 transition flex items-center gap-1.5 cursor-pointer font-medium"
              title="Teljes visszaállítás a forráskódbeli gyári megnevezésekre és alapértékekre"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>Gyári alaphelyzet</span>
            </button>
            )}
            <span className="text-xs text-slate-400 hidden sm:inline">
              A módosítások azonnal, újratöltés nélkül érvénybe lépnek a felületen.
            </span>
          </div>

          <div className="flex items-center gap-2.5">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-xs text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 transition font-medium cursor-pointer"
            >
              Mégse
            </button>

            <button
              onClick={handleSave}
              className={`px-5 py-2.5 rounded-xl text-xs font-bold text-white transition flex items-center gap-2 shadow-md cursor-pointer ${
                savedFeedback
                  ? 'bg-emerald-600 hover:bg-emerald-500'
                  : 'bg-indigo-600 hover:bg-indigo-500 dark:bg-cyan-600 dark:hover:bg-cyan-500'
              }`}
            >
              <Check className="w-4 h-4" />
              <span>{savedFeedback ? 'Sikeresen alkalmazva!' : 'Módosítások mentése & Alkalmazás'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
